import { useMemo, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useStore } from "@/hooks/useStore";
import { Panel, PageTitle } from "@/components/ui/Panel";
import { Input, Label, Select, Textarea } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { uid, nowIso } from "@/lib/utils";
import { toast } from "sonner";
import type { Deck, DeckEntry } from "@/types/deck";
import type { Card } from "@/types/card";
import {
  checkDeckLegality,
  copyCapForCard,
  isCardEligibleForDeck,
  totalEquipmentValue,
  championEquipmentStat,
  MAIN_DECK_SIZE,
  SIGNATURE_MOVES_REQUIRED,
} from "@/lib/deckLegality";

function emptyDeck(): Deck {
  return {
    id: uid("d_"),
    deckName: "New Deck",
    notes: "",
    championId: null,
    armorId: null,
    signatureMoveIds: [],
    equipmentIds: [],
    mainDeck: [],
    legality: { status: "legal", errors: [] },
    createdAt: nowIso(),
    updatedAt: nowIso(),
  };
}

// ─── Search helper ───────────────────────────────────────────────────────────
// Build one big haystack per card so a single text search hits name, type,
// faction, weight, ability text — anything the player might recall.
function cardHaystack(c: Card): string {
  const bits: string[] = [
    c.name ?? "",
    c.cardType ?? "",
    c.set ?? "",
    c.rarity ?? "",
    c.designerNotes ?? "",
    c.rulesText ?? "",
    ...(c.keywords ?? []),
    ...(c.attackKeywords ?? []),
    ...(c.defendKeywords ?? []),
    ...(c.mechanics ?? []),
    c.championFields?.ability ?? "",
    c.championFields?.faction ?? "",
    c.armorFields?.ability ?? "",
    c.armorFields?.weight ?? "",
    c.equipmentFields?.ability ?? "",
    c.equipmentFields?.attackType ?? "",
    c.equipmentFields?.defendType ?? "",
    c.equipmentFields?.equipmentSubtype ?? "",
    c.signatureMoveFields?.ability ?? "",
    c.stanceFields?.ability ?? "",
    c.utilityFields?.ability ?? "",
    c.attackDefendFields?.attackAbility ?? "",
    c.attackDefendFields?.defendAbility ?? "",
    c.attackDefendFields?.counterAbility ?? "",
  ];
  return bits.join(" ").toLowerCase();
}

function matchesSearch(c: Card, q: string): boolean {
  if (!q.trim()) return true;
  return cardHaystack(c).includes(q.trim().toLowerCase());
}

// ─── Stat chip ───────────────────────────────────────────────────────────────
// Tiny labeled value used inside CardTile. Renders nothing if value is empty
// so we don't clutter tiles with blank rows.
function Stat({ label, value }: { label: string; value: string | number | undefined | null }) {
  if (value === undefined || value === null || value === "") return null;
  return (
    <div className="flex items-baseline justify-between gap-2 text-[11px]">
      <span className="text-[#64748b]">{label}</span>
      <span className="text-[#cbd5e1] font-medium">{String(value)}</span>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-amber-soft/70 mb-0.5">
        {title}
      </p>
      <p className="text-[11px] text-[#cbd5e1] whitespace-pre-wrap leading-snug">
        {children}
      </p>
    </div>
  );
}

// ─── CardTile ─────────────────────────────────────────────────────────────────
// Full-detail card tile. Always shows: image, name, type/status header, every
// stat that's defined for that card type, and every ability/effect/counter
// blob. No hover required — everything is visible up front.
function CardTile({
  card,
  selected,
  disabled,
  onClick,
  count,
  onInc,
  onDec,
  meta,
}: {
  card: Card;
  selected?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  count?: number; // when provided, renders +/- counter instead of a toggle
  onInc?: () => void;
  onDec?: () => void;
  meta?: string; // small extra label, e.g. "val 2" for equipment
}) {
  const af = card.attackDefendFields;
  const ef = card.equipmentFields;
  const sf = card.signatureMoveFields;
  const cf = card.championFields;
  const ar = card.armorFields;
  const stf = card.stanceFields;
  const uf = card.utilityFields;

  const keywords = [
    ...(card.keywords ?? []),
    ...(card.attackKeywords ?? []).map((k) => `↑${k}`),
    ...(card.defendKeywords ?? []).map((k) => `↓${k}`),
  ];

  const counterAbility = af?.counterAbility?.trim();

  // Hand-pick a stable display order. Any card type missing a field renders
  // nothing for that row (Stat returns null on empty), so this single layout
  // covers every card type cleanly.
  return (
    <div
      className={`flex w-64 shrink-0 flex-col rounded-lg border bg-[#0f1117] transition ${
        selected
          ? "border-amber/60 ring-1 ring-amber/40"
          : disabled
          ? "border-[#2a2f3a] opacity-40"
          : "border-[#2a2f3a] hover:border-amber/30"
      }`}
    >
      {/* Image */}
      {card.imageDataUrl ? (
        <img
          src={card.imageDataUrl}
          alt={card.name}
          className="h-40 w-full rounded-t-lg border-b border-[#2a2f3a] object-cover"
        />
      ) : (
        <div className="flex h-40 w-full items-center justify-center rounded-t-lg border-b border-[#2a2f3a] bg-[#1a1d24] text-xs text-[#64748b]">
          no image
        </div>
      )}

      {/* Header */}
      <div className="px-3 pt-2">
        <p className="truncate font-semibold text-sm text-amber-soft">{card.name}</p>
        <p className="text-[10px] text-[#64748b]">
          {card.cardType}
          {card.cardType === "Attack/Defend" && af?.isSpecialMove ? " · Special Move" : ""}
          {card.status ? ` · ${card.status}` : ""}
          {meta ? ` · ${meta}` : ""}
        </p>
      </div>

      {/* Stats — only the ones that apply to this card */}
      <div className="flex-1 space-y-2 px-3 py-2">
        <div className="space-y-0.5">
          {/* Attack/Defend */}
          <Stat label="Stamina" value={af?.staminaCost} />
          <Stat label="Power" value={af?.powerValue} />
          <Stat label="Block" value={af?.blockValue} />
          <Stat label="Atk type" value={af?.attackType} />
          <Stat label="Def type" value={af?.defendType} />
          <Stat label="Weight" value={af?.weight} />
          {/* Champion */}
          <Stat label="Speed" value={cf?.speed} />
          <Stat label="Faction" value={cf?.faction} />
          <Stat label="Equip stat" value={cf?.equipmentStat} />
          <Stat label="Type" value={cf?.championType?.join(", ")} />
          {/* Armor */}
          <Stat label="Weight" value={ar?.weight} />
          <Stat label="Block" value={ar?.blockValue} />
          {/* Equipment */}
          <Stat label="Equip val" value={ef?.equipmentValue} />
          <Stat label="Subtype" value={ef?.equipmentSubtype} />
          <Stat label="2nd subtype" value={ef?.secondarySubtype} />
          <Stat label="Atk type" value={ef?.attackType} />
          <Stat label="Def type" value={ef?.defendType} />
          <Stat label="Power" value={ef?.powerValue} />
          <Stat label="Block" value={ef?.blockValue} />
          <Stat label="Stamina" value={ef?.staminaCost} />
          <Stat label="Atk weight" value={ef?.attackWeight} />
          <Stat label="Def weight" value={ef?.defendWeight} />
          {/* Signature move */}
          <Stat label="Stamina" value={sf?.staminaCost} />
          <Stat label="Power" value={sf?.powerValue} />
          <Stat label="Block" value={sf?.blockValue} />
          <Stat label="Atk type" value={sf?.attackType} />
          <Stat label="Condition" value={sf?.condition} />
          {/* Utility */}
          <Stat label="Util type" value={uf?.utilityType} />
        </div>

        {/* Keywords */}
        {keywords.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {keywords.map((kw, i) => (
              <span
                key={i}
                className="rounded bg-amber/10 px-1.5 py-0.5 text-[10px] text-amber-soft border border-amber/30"
              >
                {kw}
              </span>
            ))}
          </div>
        )}

        {/* Abilities */}
        {cf?.ability && <Section title="Champion ability">{cf.ability}</Section>}
        {ar?.ability && <Section title="Armor ability">{ar.ability}</Section>}
        {ef?.ability && <Section title="Equipment ability">{ef.ability}</Section>}
        {sf?.ability && <Section title="Signature ability">{sf.ability}</Section>}
        {stf?.ability && <Section title="Stance ability">{stf.ability}</Section>}
        {uf?.ability && <Section title="Utility ability">{uf.ability}</Section>}
        {af?.attackAbility && <Section title="Attack effect">{af.attackAbility}</Section>}
        {af?.defendAbility && <Section title="Defend effect">{af.defendAbility}</Section>}
        {counterAbility && <Section title="Counter">{counterAbility}</Section>}
        {card.rulesText && <Section title="Rules text">{card.rulesText}</Section>}

        {/* Mechanics tags */}
        {card.mechanics && card.mechanics.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {card.mechanics.map((m, i) => (
              <span
                key={i}
                className="rounded bg-[#22262f] px-1.5 py-0.5 text-[10px] text-[#94a3b8]"
              >
                {m}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Footer action: either a toggle button or +/- counter */}
      <div className="border-t border-[#2a2f3a] p-2">
        {typeof count === "number" ? (
          <div className="flex items-center justify-between">
            <Button
              size="sm"
              variant="ghost"
              disabled={count === 0}
              onClick={onDec}
            >
              −
            </Button>
            <span className="text-sm font-semibold">
              {count} <span className="text-[10px] text-[#64748b]">in deck</span>
            </span>
            <Button
              size="sm"
              variant="ghost"
              disabled={disabled}
              onClick={onInc}
            >
              +
            </Button>
          </div>
        ) : (
          <Button
            size="sm"
            variant={selected ? "primary" : "ghost"}
            disabled={disabled && !selected}
            onClick={onClick}
            className="w-full"
          >
            {selected ? "✓ Selected" : disabled ? "—" : "Select"}
          </Button>
        )}
      </div>
    </div>
  );
}

// Horizontal-scroll row for a list of CardTiles.
function TileRow({ children, empty }: { children: React.ReactNode; empty?: boolean }) {
  if (empty) {
    return (
      <div className="rounded border border-dashed border-[#2a2f3a] p-6 text-center text-xs text-[#64748b]">
        no matches
      </div>
    );
  }
  return (
    <div className="flex gap-3 overflow-x-auto pb-2" style={{ scrollbarWidth: "thin" }}>
      {children}
    </div>
  );
}

// Small thumbnail used for the "current main deck" summary at the bottom.
function CardThumb({ card, size = 40 }: { card: Card; size?: number }) {
  if (card.imageDataUrl) {
    return (
      <img
        src={card.imageDataUrl}
        alt={card.name}
        className="rounded border border-[#2a2f3a] object-cover"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      className="flex items-center justify-center rounded border border-[#2a2f3a] bg-[#1a1d24] text-[10px] text-[#64748b]"
      style={{ width: size, height: size }}
    >
      no img
    </div>
  );
}

// Generic search + status filter bar ──────────────────────────────────────────
function PoolFilters({
  query,
  setQuery,
  status,
  setStatus,
}: {
  query: string;
  setQuery: (s: string) => void;
  status: "all" | "Testing" | "Finalized";
  setStatus: (s: "all" | "Testing" | "Finalized") => void;
}) {
  return (
    <div className="mb-2 grid gap-2 sm:grid-cols-[1fr_140px]">
      <Input
        placeholder="Search by name, ability, keyword…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <Select value={status} onChange={(e) => setStatus(e.target.value as any)}>
        <option value="all">All eligible</option>
        <option value="Testing">Testing</option>
        <option value="Finalized">Finalized</option>
      </Select>
    </div>
  );
}

function applyStatusFilter(
  cards: Card[],
  status: "all" | "Testing" | "Finalized",
): Card[] {
  if (status === "all") return cards;
  return cards.filter((c) => c.status === status);
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export function DeckEditor() {
  const { id } = useParams();
  const editing = Boolean(id);
  const { state, setState } = useStore();
  const navigate = useNavigate();
  const [deck, setDeck] = useState<Deck>(() => {
    if (id) {
      const d = state.decks.find((x) => x.id === id);
      return d ? structuredClone(d) : emptyDeck();
    }
    return emptyDeck();
  });

  useEffect(() => {
    if (id && state.decks.length && deck.deckName === "New Deck") {
      const d = state.decks.find((x) => x.id === id);
      if (d) setDeck(structuredClone(d));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.decks.length]);

  // Only Testing or Finalized cards are visible in the deck builder.
  const eligibleCards = useMemo(
    () => state.cards.filter(isCardEligibleForDeck),
    [state.cards],
  );

  const champions = useMemo(
    () => eligibleCards.filter((c) => c.cardType === "Champion"),
    [eligibleCards],
  );
  const armors = useMemo(
    () => eligibleCards.filter((c) => c.cardType === "Armor"),
    [eligibleCards],
  );
  const sigs = useMemo(
    () => eligibleCards.filter((c) => c.cardType === "Signature Move"),
    [eligibleCards],
  );
  const equipment = useMemo(
    () => eligibleCards.filter((c) => c.cardType === "Equipment"),
    [eligibleCards],
  );
  const mainPool = useMemo(
    () =>
      eligibleCards.filter((c) =>
        ["Attack/Defend", "Stance", "Utility"].includes(c.cardType),
      ),
    [eligibleCards],
  );

  // Map across the FULL card library (not just eligible) so we can still resolve
  // any card a deck already references — even if its status changed.
  const cardMap = useMemo(
    () => new Map(state.cards.map((c) => [c.id, c])),
    [state.cards],
  );

  // ─── Per-section search/filter state ───────────────────────────────────
  const [champQ, setChampQ] = useState("");
  const [champStatus, setChampStatus] = useState<"all" | "Testing" | "Finalized">("all");
  const [armorQ, setArmorQ] = useState("");
  const [armorStatus, setArmorStatus] = useState<"all" | "Testing" | "Finalized">("all");
  const [sigQ, setSigQ] = useState("");
  const [sigStatus, setSigStatus] = useState<"all" | "Testing" | "Finalized">("all");
  const [equipQ, setEquipQ] = useState("");
  const [equipStatus, setEquipStatus] = useState<"all" | "Testing" | "Finalized">("all");
  const [mainQ, setMainQ] = useState("");
  const [mainStatus, setMainStatus] = useState<"all" | "Testing" | "Finalized">("all");
  const [mainTypeFilter, setMainTypeFilter] = useState<"all" | "Stance" | "Utility" | "Attack/Defend">(
    "all",
  );

  // ─── Patches ───────────────────────────────────────────────────────────
  function patch(p: Partial<Deck>) {
    setDeck((d) => ({ ...d, ...p }));
  }

  function bumpMain(cardId: string, delta: number) {
    setDeck((d) => {
      const cur = d.mainDeck.find((e) => e.cardId === cardId);
      let next: DeckEntry[];
      if (!cur) next = delta > 0 ? [...d.mainDeck, { cardId, count: delta }] : d.mainDeck;
      else {
        const c2 = cur.count + delta;
        next =
          c2 <= 0
            ? d.mainDeck.filter((e) => e.cardId !== cardId)
            : d.mainDeck.map((e) =>
                e.cardId === cardId ? { ...e, count: c2 } : e,
              );
      }
      return { ...d, mainDeck: next };
    });
  }

  function toggleSig(cardId: string) {
    setDeck((d) => {
      const has = d.signatureMoveIds.includes(cardId);
      // Block adding past the cap; always allow removing.
      if (!has && d.signatureMoveIds.length >= SIGNATURE_MOVES_REQUIRED) {
        toast.error(`Max ${SIGNATURE_MOVES_REQUIRED} signature moves`);
        return d;
      }
      return {
        ...d,
        signatureMoveIds: has
          ? d.signatureMoveIds.filter((x) => x !== cardId)
          : [...d.signatureMoveIds, cardId],
      };
    });
  }

  function toggleEquip(cardId: string) {
    setDeck((d) => ({
      ...d,
      equipmentIds: d.equipmentIds.includes(cardId)
        ? d.equipmentIds.filter((x) => x !== cardId)
        : [...d.equipmentIds, cardId],
    }));
  }

  function save() {
    const legality = checkDeckLegality(deck, cardMap);
    const updated: Deck = { ...deck, legality, updatedAt: nowIso() };
    setState((s) => {
      const exists = s.decks.some((d) => d.id === updated.id);
      return {
        ...s,
        decks: exists
          ? s.decks.map((d) => (d.id === updated.id ? updated : d))
          : [...s.decks, updated],
      };
    });
    if (legality.status === "illegal") {
      toast.warning(`Deck saved as illegal — ${legality.errors.length} issue(s)`);
    } else {
      toast.success(editing ? "Deck saved" : "Deck created");
    }
    navigate("/decks");
  }

  // ─── Live legality + budget readouts ───────────────────────────────────
  const liveLegality = useMemo(
    () => checkDeckLegality(deck, cardMap),
    [deck, cardMap],
  );

  const champion = deck.championId ? cardMap.get(deck.championId) : undefined;
  const equipCap = championEquipmentStat(champion);
  const equippedCards = deck.equipmentIds
    .map((id) => cardMap.get(id))
    .filter((c): c is Card => Boolean(c));
  const equipUsed = totalEquipmentValue(equippedCards);
  const equipOver = equipUsed > equipCap;

  const mainTotal = deck.mainDeck.reduce((a, e) => a + e.count, 0);

  // ─── Filtered pools per section ────────────────────────────────────────
  const champList = applyStatusFilter(champions, champStatus).filter((c) =>
    matchesSearch(c, champQ),
  );
  const armorList = applyStatusFilter(armors, armorStatus).filter((c) =>
    matchesSearch(c, armorQ),
  );
  const sigList = applyStatusFilter(sigs, sigStatus).filter((c) =>
    matchesSearch(c, sigQ),
  );
  const equipList = applyStatusFilter(equipment, equipStatus).filter((c) =>
    matchesSearch(c, equipQ),
  );
  const mainList = applyStatusFilter(mainPool, mainStatus)
    .filter((c) => matchesSearch(c, mainQ))
    .filter((c) => mainTypeFilter === "all" || c.cardType === mainTypeFilter);

  // ─── Render ────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <PageTitle>{editing ? "Edit deck" : "New deck"}</PageTitle>
        <div className="flex items-center gap-2">
          <Badge tone={liveLegality.status === "legal" ? "green" : "red"}>
            {liveLegality.status === "legal" ? "Legal" : "Illegal"}
          </Badge>
          <Button onClick={save}>Save</Button>
        </div>
      </div>

      {/* Legality readout — always visible */}
      {liveLegality.errors.length > 0 && (
        <Panel className="border-red-500/40 bg-red-500/5">
          <Label>Issues ({liveLegality.errors.length})</Label>
          <ul className="mt-1 list-disc pl-5 text-xs text-red-300 space-y-0.5">
            {liveLegality.errors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </Panel>
      )}

      {/* Deck meta */}
      <Panel className="grid gap-3 sm:grid-cols-2">
        <div>
          <Label>Deck name</Label>
          <Input
            value={deck.deckName}
            onChange={(e) => patch({ deckName: e.target.value })}
          />
        </div>
        <div>
          <Label>Notes</Label>
          <Textarea
            rows={1}
            value={deck.notes ?? ""}
            onChange={(e) => patch({ notes: e.target.value })}
          />
        </div>
      </Panel>

      {/* Champion */}
      <Panel>
        <div className="flex items-center justify-between">
          <Label>
            Champion (1) {champion ? `— ${champion.name}` : ""}
          </Label>
          {champion && (
            <button
              className="text-xs text-[#64748b] hover:text-amber-soft"
              onClick={() => patch({ championId: null })}
            >
              clear
            </button>
          )}
        </div>
        <div className="mt-2">
          <PoolFilters
            query={champQ}
            setQuery={setChampQ}
            status={champStatus}
            setStatus={setChampStatus}
          />
          <TileRow empty={champList.length === 0}>
            {champList.map((c) => (
              <CardTile
                key={c.id}
                card={c}
                selected={deck.championId === c.id}
                onClick={() =>
                  patch({ championId: deck.championId === c.id ? null : c.id })
                }
              />
            ))}
          </TileRow>
        </div>
      </Panel>

      {/* Armor */}
      <Panel>
        <div className="flex items-center justify-between">
          <Label>
            Armor (1)
            {deck.armorId && cardMap.get(deck.armorId)
              ? ` — ${cardMap.get(deck.armorId)!.name}`
              : ""}
          </Label>
          {deck.armorId && (
            <button
              className="text-xs text-[#64748b] hover:text-amber-soft"
              onClick={() => patch({ armorId: null })}
            >
              clear
            </button>
          )}
        </div>
        <div className="mt-2">
          <PoolFilters
            query={armorQ}
            setQuery={setArmorQ}
            status={armorStatus}
            setStatus={setArmorStatus}
          />
          <TileRow empty={armorList.length === 0}>
            {armorList.map((c) => (
              <CardTile
                key={c.id}
                card={c}
                selected={deck.armorId === c.id}
                onClick={() =>
                  patch({ armorId: deck.armorId === c.id ? null : c.id })
                }
              />
            ))}
          </TileRow>
        </div>
      </Panel>

      {/* Signature moves */}
      <Panel>
        <div className="flex items-center justify-between">
          <Label>
            Signature moves ({deck.signatureMoveIds.length}/
            {SIGNATURE_MOVES_REQUIRED})
          </Label>
          <Badge
            tone={
              deck.signatureMoveIds.length === SIGNATURE_MOVES_REQUIRED
                ? "green"
                : "red"
            }
          >
            {deck.signatureMoveIds.length === SIGNATURE_MOVES_REQUIRED
              ? "ok"
              : `need ${SIGNATURE_MOVES_REQUIRED}`}
          </Badge>
        </div>
        <div className="mt-2">
          <PoolFilters
            query={sigQ}
            setQuery={setSigQ}
            status={sigStatus}
            setStatus={setSigStatus}
          />
          <TileRow empty={sigList.length === 0}>
            {sigList.map((c) => {
              const active = deck.signatureMoveIds.includes(c.id);
              const atCap =
                !active &&
                deck.signatureMoveIds.length >= SIGNATURE_MOVES_REQUIRED;
              return (
                <CardTile
                  key={c.id}
                  card={c}
                  selected={active}
                  disabled={atCap}
                  onClick={() => toggleSig(c.id)}
                />
              );
            })}
          </TileRow>
        </div>
      </Panel>

      {/* Equipment */}
      <Panel>
        <div className="flex items-center justify-between">
          <Label>Equipment</Label>
          <Badge tone={equipOver ? "red" : equipCap === 0 ? "default" : "green"}>
            {equipUsed}/{equipCap || "?"} used
          </Badge>
        </div>
        {!champion && (
          <p className="mt-1 text-[11px] text-[#64748b]">
            Pick a champion to set the equipment budget.
          </p>
        )}
        <div className="mt-2">
          <PoolFilters
            query={equipQ}
            setQuery={setEquipQ}
            status={equipStatus}
            setStatus={setEquipStatus}
          />
          <TileRow empty={equipList.length === 0}>
            {equipList.map((c) => {
              const active = deck.equipmentIds.includes(c.id);
              const cardCost =
                parseInt(c.equipmentFields?.equipmentValue ?? "0", 10) || 0;
              const wouldOverflow = !active && equipUsed + cardCost > equipCap;
              return (
                <CardTile
                  key={c.id}
                  card={c}
                  selected={active}
                  disabled={wouldOverflow}
                  meta={`val ${cardCost || "?"}`}
                  onClick={() => toggleEquip(c.id)}
                />
              );
            })}
          </TileRow>
        </div>
      </Panel>

      {/* Main deck pool */}
      <Panel>
        <div className="flex items-center justify-between">
          <Label>
            Main deck — {mainTotal}/{MAIN_DECK_SIZE}
          </Label>
          <Badge tone={mainTotal === MAIN_DECK_SIZE ? "green" : "red"}>
            {mainTotal === MAIN_DECK_SIZE
              ? "ok"
              : mainTotal > MAIN_DECK_SIZE
              ? `over by ${mainTotal - MAIN_DECK_SIZE}`
              : `need ${MAIN_DECK_SIZE - mainTotal} more`}
          </Badge>
        </div>
        <div className="mt-2 grid gap-2 sm:grid-cols-[1fr_140px_140px]">
          <Input
            placeholder="Search by name, ability, keyword…"
            value={mainQ}
            onChange={(e) => setMainQ(e.target.value)}
          />
          <Select
            value={mainTypeFilter}
            onChange={(e) => setMainTypeFilter(e.target.value as any)}
          >
            <option value="all">All types</option>
            <option value="Attack/Defend">Attack/Defend</option>
            <option value="Stance">Stance</option>
            <option value="Utility">Utility</option>
          </Select>
          <Select
            value={mainStatus}
            onChange={(e) => setMainStatus(e.target.value as any)}
          >
            <option value="all">All eligible</option>
            <option value="Testing">Testing</option>
            <option value="Finalized">Finalized</option>
          </Select>
        </div>
        <div className="mt-2">
          <TileRow empty={mainList.length === 0}>
            {mainList.map((c) => {
              const cur =
                deck.mainDeck.find((e) => e.cardId === c.id)?.count ?? 0;
              const cap = copyCapForCard(c);
              const atMax = cur >= cap;
              return (
                <CardTile
                  key={c.id}
                  card={c}
                  count={cur}
                  disabled={atMax}
                  meta={`max ${cap}`}
                  onInc={() => bumpMain(c.id, 1)}
                  onDec={() => bumpMain(c.id, -1)}
                />
              );
            })}
          </TileRow>
        </div>
      </Panel>

      {/* Current main deck summary */}
      <Panel>
        <Label>Current main deck</Label>
        {deck.mainDeck.length === 0 ? (
          <p className="text-xs text-[#64748b]">empty</p>
        ) : (
          <ul className="grid gap-1 sm:grid-cols-2">
            {deck.mainDeck.map((e) => {
              const c = cardMap.get(e.cardId);
              if (!c) {
                return (
                  <li key={e.cardId} className="text-xs text-red-400">
                    {e.count}× missing card ({e.cardId})
                  </li>
                );
              }
              return (
                <li
                  key={e.cardId}
                  className="flex items-center gap-2 rounded border border-[#22262f] px-2 py-1 text-sm"
                >
                  <CardThumb card={c} size={28} />
                  <span className="truncate">
                    {e.count}× {c.name}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </Panel>
    </div>
  );
}
